import { useEffect, useRef, useState } from 'react';
import { HeaderSearchCategory, HeaderSearchResultItem } from './headerSearchConstants';

interface UseSearchCategoryOptions<TResponse> {
	category: HeaderSearchCategory;
	activeCategory: HeaderSearchCategory;
	searchTerm: string;
	fetcher: (term: string) => Promise<TResponse>;
	mapItems: (response: TResponse) => HeaderSearchResultItem[];
	/** Fetch once when the category is first activated, then filter/slice client-side (News, Articles). */
	fetchOnce?: boolean;
	/** Debounce refetches by this many ms while the user is typing (Circulars, Blogs, Stocks). */
	debounceMs?: number;
	/** Extra gate beyond "category is active" (Stocks also requires the dropdown to be open). Defaults to true. */
	isEnabled?: boolean;
	/** Reuse the last empty-term result instead of refetching when the term is cleared (Stocks). */
	cacheEmptyTermResult?: boolean;
}

interface UseSearchCategoryResult {
	items: HeaderSearchResultItem[];
	isLoading: boolean;
}

/**
 * Shared fetch/debounce/stale-guard logic for the header search dropdown's category tabs.
 * Extracted because Stocks/Blogs/Circulars/News/Articles previously duplicated this ~35-line
 * effect five times, differing only in the fetcher/mapper and a couple of flags.
 */
export const useSearchCategory = <TResponse>({
	category,
	activeCategory,
	searchTerm,
	fetcher,
	mapItems,
	fetchOnce = false,
	debounceMs = 0,
	isEnabled = true,
	cacheEmptyTermResult = false,
}: UseSearchCategoryOptions<TResponse>): UseSearchCategoryResult => {
	const [items, setItems] = useState<HeaderSearchResultItem[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isFetched, setIsFetched] = useState(false);
	const defaultResultRef = useRef<HeaderSearchResultItem[] | null>(null);

	// Read via refs (not effect deps) so callers can pass inline arrow functions
	// without retriggering the fetch on every render.
	const fetcherRef = useRef(fetcher);
	fetcherRef.current = fetcher;
	const mapItemsRef = useRef(mapItems);
	mapItemsRef.current = mapItems;

	useEffect(() => {
		if (activeCategory !== category || !isEnabled) {
			return;
		}

		if (fetchOnce) {
			if (isFetched) {
				return;
			}
			setIsLoading(true);
			fetcherRef
				.current(searchTerm)
				.then((response) => setItems(mapItemsRef.current(response)))
				.catch(() => setItems([]))
				.finally(() => {
					setIsLoading(false);
					setIsFetched(true);
				});
			return;
		}

		if (cacheEmptyTermResult && !searchTerm && defaultResultRef.current) {
			setItems(defaultResultRef.current);
			setIsLoading(false);
			return;
		}

		let isStale = false;
		setIsLoading(true);
		const timeoutId = setTimeout(
			() => {
				fetcherRef
					.current(searchTerm)
					.then((response) => {
						if (isStale) {
							return;
						}
						const mapped = mapItemsRef.current(response);
						if (cacheEmptyTermResult && !searchTerm) {
							defaultResultRef.current = mapped;
						}
						setItems(mapped);
					})
					.catch(() => {
						if (!isStale) {
							setItems([]);
						}
					})
					.finally(() => {
						if (!isStale) {
							setIsLoading(false);
						}
					});
			},
			searchTerm ? debounceMs : 0,
		);

		return () => {
			isStale = true;
			clearTimeout(timeoutId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeCategory, category, searchTerm, isEnabled, fetchOnce, cacheEmptyTermResult, isFetched]);

	return { items, isLoading };
};
